import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, Button, message, Table, Tag, Select, Typography, Space, Modal, Form, Input, TimePicker, Popconfirm, Badge, Segmented, Dropdown, Switch, Upload, Collapse, Tooltip } from "antd";
import Alert from "../components/GxAlert";
import GxTable from "../components/GxTable";
import { EditOutlined, DeleteOutlined, UserAddOutlined, ReloadOutlined, DownOutlined } from "@ant-design/icons";
import {
  importAttendanceFile, previewAttendanceFile, getAttendance, getEmployees,
  updateAttendancePin, deleteAttendancePin,
  getRawPunches, getUnmappedDevices, mapDevice, setDeviceLabel,
  getAttendanceMonthlySummary,
  getAttendancePeriodReport,
  getAttendanceMonthlyStatus, finalizeAttendanceMonth, manualCorrectDay, reopenAttendanceMonth,
  previewDeviceRoster, applyDeviceRoster,
  getAttendanceMonthData, deleteAttendanceMonthData, getAttendanceMissingEmployees,
  getAccountingSettings,
} from "../api/client";
import ImportExportBar from "../components/ImportExportBar";
import ExportButton from "../components/ExportButton";
import HelpPopover from "../components/HelpPopover";
import GxNote from "../components/GxNote";
import WeeklyGrid from "./attendance/WeeklyGrid";
import AttendanceSettingsTab from "./attendance/AttendanceSettingsTab";
import PersonDaysPanel from "./attendance/PersonDaysPanel";
import ManualMonthTab from "./attendance/ManualMonthTab";
import DeviceCardsTab from "./attendance/DeviceCardsTab";
import { stMeta, fa, hhmm, minutesLabel } from "./attendance/attendanceMeta";
import { byNum, byStr, byDate, byTime } from "../utils/sorters";
import { loadOrder, saveOrder } from "../utils/layoutStore";
import { JALALI_MONTHS, JALALI_YEARS, currentJalali, toJalali, formatDurationFa, JalaliDateInput, todayISO } from "../utils/dateUtils";
import NewStaffTag from "../components/NewStaffTag";
import { filterByChildren } from "../utils/selectFilter";
import dayjs from "dayjs";
import { faNum } from "../utils/faNumberFormat";
import { getActiveYm, setActiveYm } from "../utils/activeMonth";

const { Title, Text } = Typography;
const { Option } = Select;

export default function AttendancePage() {
  const cj = currentJalali();
  // لینکِ «اصلاح حضور» از صفحهٔ حقوق: /attendance?year=&month=&tab=review&only_problems=1
  const [searchParams, setSearchParams] = useSearchParams();
  const qsNum = (k, dflt) => {
    const v = Number(searchParams.get(k));
    return Number.isFinite(v) && v > 0 ? v : dflt;
  };
  const [records, setRecords] = useState([]);
  const [empMap, setEmpMap] = useState({});
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  // پیش‌نمایشِ ایمپورت — تا تأیید نشود چیزی ذخیره نمی‌شود
  const [preview, setPreview] = useState(null);   // {data, file}
  const [applying, setApplying] = useState(false);
  // آنچه از ماهِ انتخاب‌شده در سیستم هست + پرسنلی که ترددشان نیامده
  const [monthData, setMonthData] = useState(null);
  // مبنای «هر N ساعت کسری = یک روز» — همان عددی که موتورِ حقوق استفاده می‌کند
  const [baseHours, setBaseHours] = useState(7);
  const [missing, setMissing] = useState(null);
  /* ماهِ انتخابی یادش می‌ماند (v2.112.0): آدرس ← آخرین انتخابِ کاربر ← ماهِ جاری. قبلاً با هر بار
     آمدن به صفحه به ماهِ جاری برمی‌گشت و کسی که روی شهریور کار می‌کرد باید دوباره انتخابش می‌کرد. */
  const savedYm = getActiveYm() || loadOrder("ym", "attendance") || {};   // ماهِ فعالِ مشترک (v2.132.0)
  const [year, setYearState] = useState(() => qsNum("year", savedYm.year || cj.year));
  const [month, setMonthState] = useState(() => qsNum("month", savedYm.month || cj.month));
  const keepYm = (y, m) => {
    saveOrder("ym", "attendance", { year: y, month: m });
    setActiveYm(y, m);
    setSearchParams((prev) => { const n = new URLSearchParams(prev); n.set("year", y); n.set("month", m); return n; },
      { replace: true });
  };
  const setYear = (y) => { setYearState(y); keepYm(y, month); };
  const setMonth = (m) => { setMonthState(m); keepYm(year, m); };
  const [editRec, setEditRec] = useState(null);
  const [form] = Form.useForm();
  // افزودنِ دستیِ رکورد حضور در لیست
  const [addOpen, setAddOpen] = useState(false);
  const [addForm] = Form.useForm();

  // ثبت‌های خام دستگاه و شناسه‌های بدون پرسنل
  const [rawPunches, setRawPunches] = useState([]);
  const [rawLoading, setRawLoading] = useState(false);
  const [unmapped, setUnmapped] = useState([]);
  const [mapModal, setMapModal] = useState({ open: false, device_uid: null });
  const [mapEmpId, setMapEmpId] = useState(null);
  const [labelModal, setLabelModal] = useState({ open: false, device_uid: null });
  const [labelText, setLabelText] = useState("");
  /* لیستِ نفراتِ ماه — آکاردئونی و **تک‌بازشو**: باز کردنِ یک نفر، نفرِ قبلی
     را می‌بندد. با چند پنلِ همزمانِ باز، صفحه چند هزار ردیف می‌شد و کاربر
     دیگر نمی‌فهمید کدام عدد مالِ کیست. */
  // نفرِ بازِ مرحلهٔ ۴ هم یادش می‌ماند (آدرسِ ?emp=) — برگشت به صفحه همان نفر را باز می‌کند
  const [openEmp, setOpenEmpState] = useState(() => {
    const v = Number(searchParams.get("emp") || loadOrder("emp", "attendance"));
    return Number.isFinite(v) && v > 0 ? v : null;
  });
  const openEmpRef = useRef(openEmp);
  const setOpenEmp = (upd) => {
    const v = typeof upd === "function" ? upd(openEmpRef.current) : upd;
    openEmpRef.current = v;
    setOpenEmpState(v);
    saveOrder("emp", "attendance", v);
    setSearchParams((prev) => { const n = new URLSearchParams(prev); if (v) n.set("emp", v); else n.delete("emp"); return n; },
      { replace: true });
  };
  const personTopRef = useRef(null);
  /* تبِ فعال per-user یادش می‌ماند: کاربر وسطِ کارِ یک مرحله صفحه را می‌بندد و
     انتظار دارد همان‌جا برگردد، نه سرِ خطِ اول. لینکِ صریح (?tab=) مقدم است. */
  /* تبِ فعال در **آدرس** می‌نشیند تا «برگشت»ِ مرورگر به مرحلهٔ قبلیِ همین
     صفحه برگردد، نه به صفحهٔ قبلیِ منو. مقدارِ به‌یادمانده فقط وقتی به کار
     می‌آید که آدرس چیزی نگوید (بازکردنِ تازهٔ صفحه). */
  const [rememberedTab] = useState(() => loadOrder("atab", "attendance") || "import");
  const activeTab = searchParams.get("tab") || rememberedTab;
  const setActiveTab = (k) => setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    next.set("tab", k);
    return next;
  });
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  // جستجوی نام/کد در لیستِ نفراتِ ماه
  const [peopleSearch, setPeopleSearch] = useState("");
  // فقط کسانی که هنوز کارِ رسیدگی‌نشده دارند (لینکِ ?only_problems=1)
  const [onlyProblems, setOnlyProblems] = useState(
    () => searchParams.get("only_problems") === "1");
  // نگاشتِ کدِ کارتِ دستگاه (۴رقمی) ↔ کدِ پرسنلی (۵رقمی)
  const [rosterFile, setRosterFile] = useState(null);
  const [roster, setRoster] = useState(null);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [rosterOverrides, setRosterOverrides] = useState({});   // {"0009": employee_id}
  const [rosterCreate, setRosterCreate] = useState([]);          // ["0012", ...]
  // گزارشِ دوره‌ای — همان فرمِ کاغذیِ دستگاه
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportAll, setReportAll] = useState(false);   // شاملِ کارمزدی‌ها هم بشود؟

  const loadSummary = async () => {
    setSummaryLoading(true);
    try { const r = await getAttendanceMonthlySummary({ year, month }); setSummaryData(r.data); }
    catch { message.error("خطا در بارگذاری خلاصه"); }
    setSummaryLoading(false);
  };
  const loadPeriodReport = async () => {
    setReportLoading(true);
    try { const r = await getAttendancePeriodReport({ year, month, include_piece_rate: reportAll });
      setReportData(r.data); }
    catch { message.error("خطا در بارگذاری گزارش دوره‌ای"); }
    setReportLoading(false);
  };
  /* باز/بستنِ کاردکسِ یک نفر در همین لیست — نه پرش به تب و صفحهٔ دیگر.
     خواستهٔ صریحِ کاربر: «بدون اینکه هی صفحه‌های زیاد جدید اضافه کنیم». */
  const togglePerson = (empId) => setOpenEmp((cur) => (cur === empId ? null : empId));
  const openPerson = (empId) => {
    setOpenEmp(empId);
    saveOrder("atab", "attendance", "review");
    setActiveTab("review");
  };

  const [monthStatus, setMonthStatus] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  /* قبلاً این تابع «لیستِ بازبینی» را هم می‌گرفت — یعنی سرور برای هر بار
     عوض‌کردنِ ماه، دو بار روی همهٔ پرسنل × همهٔ روزها حلقه می‌زد و یک عددِ
     تکراری می‌داد. حالا شمارنده‌ها همه از خودِ «گزارشِ دوره‌ای» می‌آیند. */
  const loadMonthStatus = async () => {
    try {
      const s = await getAttendanceMonthlyStatus(year, month);
      setMonthStatus(s.data);
    } catch { /* silent — شمارندهٔ نهایی‌سازی حیاتی نیست */ }
  };
  // ---- نگاشتِ کدهای دستگاه ----
  const rosterPreview = async (file, overrides = rosterOverrides, createCodes = rosterCreate) => {
    setRosterLoading(true);
    try {
      const r = await previewDeviceRoster(file, overrides, createCodes);
      setRoster(r.data);
      setRosterFile(file);
    } catch (e) {
      message.error(e?.response?.data?.detail || "خطا در خواندن فایل دستگاه");
    }
    setRosterLoading(false);
  };
  const setRosterEmployee = (code, employeeId) => {
    const ov = { ...rosterOverrides };
    const cr = rosterCreate.filter((c) => c !== code);
    if (employeeId) ov[code] = employeeId; else delete ov[code];
    setRosterOverrides(ov); setRosterCreate(cr);
    if (rosterFile) rosterPreview(rosterFile, ov, cr);
  };
  const toggleRosterCreate = (code, on) => {
    const cr = on ? [...new Set([...rosterCreate, code])] : rosterCreate.filter((c) => c !== code);
    const ov = { ...rosterOverrides };
    if (on) delete ov[code];
    setRosterCreate(cr); setRosterOverrides(ov);
    if (rosterFile) rosterPreview(rosterFile, ov, cr);
  };
  const rosterApply = async () => {
    if (!rosterFile) return;
    setRosterLoading(true);
    try {
      const r = await applyDeviceRoster(rosterFile, rosterOverrides, rosterCreate);
      message.success(r.data.message);
      setRoster(null); setRosterFile(null); setRosterOverrides({}); setRosterCreate([]);
      loadEmployees(); loadUnmapped();
    } catch (e) {
      const d = e?.response?.data?.detail;
      message.error(typeof d === "string" ? d : (d?.message || "خطا در اعمال نگاشت"));
    }
    setRosterLoading(false);
  };

  const finalizeMonth = async () => {
    const unresolved = monthStatus?.unresolved_issues || 0;
    let pin;
    if (unresolved > 0) {
      pin = window.prompt(`${unresolved} مورد هنوز تصمیم‌گیری نشده. برای نهایی‌سازیِ اجباری کد مدیریتی را وارد کنید:`);
      if (!pin) return;
    }
    try {
      await finalizeAttendanceMonth({ year, month, force: unresolved > 0 }, pin);
      message.success("حضورِ ماه نهایی شد"); loadPeople();
    } catch (e) { message.error(e?.response?.data?.detail || "خطا در نهایی‌سازی"); }
  };
  /** بازگشاییِ حضورِ ماهِ نهایی‌شده با کد مدیریتی (v2.122.0) — اصلاح بعد از بستن */
  const reopenMonth = async () => {
    const pin = window.prompt("بازگشاییِ حضورِ این ماه — کد مدیریتی را وارد کنید:");
    if (!pin) return;
    try {
      const r = await reopenAttendanceMonth(year, month, pin);
      message.success(r.data?.message || "ماه بازگشایی شد"); loadPeople(); loadMonthData?.();
    } catch (e) { message.error(e?.response?.data?.detail || "بازگشایی ناموفق بود"); }
  };
  const loadEmployees = () => {
    getEmployees({ limit: 1000 }).then(r => {
      const list = Array.isArray(r.data) ? r.data : (r.data.items || []);
      setEmployees(list);
      const m = {};
      list.forEach(e => { m[e.id] = `${e.full_name} (${e.employee_code})`; });
      setEmpMap(m);
    }).catch(() => {});
  };

  useEffect(() => { loadEmployees(); }, []);
  useEffect(() => {
    getAccountingSettings()
      .then((r) => setBaseHours(Number(r.data?.overtime_base_hours_per_day) || 7))
      .catch(() => {});
  }, []);

  // با عوض‌شدنِ ماه، شناسه‌های بی‌صاحبِ همان ماه دوباره خوانده می‌شوند.
  // `loadUnmapped` عمداً اینجاست نه در TAB_LOADER: نشانِ مرحلهٔ ۲ حتی وقتی آن
  // مرحله باز نیست هم باید عدد داشته باشد.
  useEffect(() => { loadUnmapped(); loadMonthData(); /* eslint-disable-next-line */ }, [year, month]);


  const handleImport = async ({ file }) => {
    setLoading(true);
    try {
      const res = await previewAttendanceFile(file, year, month);
      setPreview({ data: res.data, file });
    } catch (e) {
      message.error(e?.response?.data?.detail || "خطا در خواندن فایل");
    }
    setLoading(false);
    return false;
  };

  const confirmImport = async () => {
    setApplying(true);
    try {
      const res = await importAttendanceFile(preview.file, year, month);
      setImportResult(res.data);
      message.success(res.data.message || "ایمپورت انجام شد");
      setPreview(null);
      loadRecords();
      loadUnmapped();
      loadMonthData();
    } catch (e) {
      message.error(e?.response?.data?.detail || "خطا در ایمپورت فایل");
    }
    setApplying(false);
  };

  const loadRecords = async () => {
    setLoading(true);
    try {
      const res = await getAttendance(year, month);
      setRecords(res.data);
    } catch {
      message.error("خطا در بارگذاری");
    }
    setLoading(false);
  };

  const loadRawPunches = async () => {
    setRawLoading(true);
    try {
      const res = await getRawPunches({ year, month });
      setRawPunches(res.data);
    } catch { message.error("خطا در بارگذاری پانچ‌ها"); }
    setRawLoading(false);
  };

  const loadMonthData = async () => {
    try {
      const [md, ms] = await Promise.all([
        getAttendanceMonthData(year, month),
        getAttendanceMissingEmployees(year, month),
      ]);
      setMonthData(md.data);
      setMissing(ms.data);
    } catch { /* silent */ }
  };

  /**
   * پاک‌کردنِ کاملِ دادهٔ حضورِ ماه.
   * چرا لازم است: اگر فایل را دوباره ایمپورت کنیم بدونِ پاک‌کردن، اعداد
   * روی هم می‌نشینند. اصلاحاتِ دستی طبقِ قاعدهٔ همیشگی حفظ می‌شوند.
   */
  const doDeleteMonth = async (keepManual) => {
    try {
      const res = await deleteAttendanceMonthData(year, month, keepManual);
      message.success(res.data?.message || "دادهٔ ماه پاک شد");
      loadRecords(); loadUnmapped(); loadMonthData(); loadRawPunches();
    } catch (e) {
      message.error(e?.response?.data?.detail || "خطا در حذفِ دادهٔ ماه");
    }
  };

  const loadUnmapped = async () => {
    try {
      // فقط ماهِ انتخاب‌شده — یک کدِ دستگاه ممکن است ماهِ بعد به نفرِ دیگری
      // داده شده باشد، پس «بی‌صاحب بودن» را باید per-ماه دید.
      const res = await getUnmappedDevices({ year, month });
      setUnmapped(res.data);
    } catch { /* silent */ }
  };

  /* 🐞 قبلاً هر تب لودرِ خودش را فقط موقعِ کلیک صدا می‌زد، پس عوض‌کردنِ ماه
     محتوای همان تب را تازه نمی‌کرد و کاربر عددِ ماهِ قبل را می‌دید.
     حالا تغییرِ ماه و تغییرِ تب یک مسیرِ کد دارند.
     ⚠️ این بلوک باید **بعد از** تعریفِ همهٔ لودرها بماند؛ بالاتر بردنش
     یعنی TDZ و صفحهٔ سفید (کامپایل هم می‌شود و خطا فقط موقعِ اجرا می‌آید). */
  const loadPeople = () => { loadPeriodReport(); loadMonthStatus(); };
  const TAB_LOADER = {
    records: loadRecords, review: loadPeople, finalize: loadPeople,
    summary: loadSummary, raw: loadRawPunches,
  };
  /* 🐞 v2.112.0 — «لودِ منوها کُند است»: هر جابه‌جاییِ مرحله دوباره همهٔ داده را می‌خواند (مرحلهٔ ۴ و ۶ هر بار
     دو درخواستِ سنگین). حالا هر بارگذار per ماه یک بار؛ جابه‌جاییِ بعدی بین مراحل فوری است. دکمه‌های
     «تازه‌سازی/نمایش» و هر ویرایش همان بارگذارها را مستقیم صدا می‌زنند و داده را تازه می‌کنند. */
  const loadedRef = useRef({});
  useEffect(() => {
    const fn = TAB_LOADER[activeTab];
    if (!fn) return;
    const key = `${year}-${month}-${reportAll}`;
    const group = { review: "people", finalize: "people" }[activeTab] || activeTab;   // مرحلهٔ ۴ و ۶ یک داده
    if (loadedRef.current[group] === key) return;
    loadedRef.current[group] = key;
    fn();
    /* eslint-disable-next-line */
  }, [year, month, activeTab]);

  const handleMapDevice = async () => {
    if (!mapEmpId) { message.warning("یک پرسنل انتخاب کنید"); return; }
    try {
      const res = await mapDevice(mapModal.device_uid, mapEmpId);
      message.success(res.data.message);
      setMapModal({ open: false, device_uid: null });
      setMapEmpId(null);
      loadUnmapped();
      loadRecords();
      loadRawPunches();
      loadMonthData();
    } catch (e) { message.error(e?.response?.data?.detail || "خطا در نگاشت"); }
  };

  const openLabel = (rec) => {
    setLabelText(rec.label || "");
    setLabelModal({ open: true, device_uid: rec.device_uid });
  };

  const handleSaveLabel = async () => {
    try {
      await setDeviceLabel(labelModal.device_uid, labelText || null);
      message.success("نام ثبت شد");
      setLabelModal({ open: false, device_uid: null });
      setLabelText("");
      loadUnmapped();
    } catch { message.error("خطا در ثبت نام"); }
  };

  const openEdit = (rec) => {
    setEditRec(rec);
    form.setFieldsValue({
      check_in: rec.check_in ? dayjs(rec.check_in, "HH:mm:ss") : null,
      check_out: rec.check_out ? dayjs(rec.check_out, "HH:mm:ss") : null,
      status: rec.status,
    });
  };

  // اگر دورهٔ آن ماه تاییدشده باشد backend خطای ۴۰۳ می‌دهد → رمز مدیریتی می‌گیریم و دوباره تلاش
  const handleSaveEdit = async () => {
    const v = await form.validateFields();
    const payload = {
      check_in: v.check_in ? v.check_in.format("HH:mm:ss") : null,
      check_out: v.check_out ? v.check_out.format("HH:mm:ss") : null,
      status: v.status,
    };
    try {
      await updateAttendancePin(editRec.id, payload);
      message.success("رکورد ویرایش شد");
      setEditRec(null);
      loadRecords();
    } catch (e) {
      if (e?.response?.status === 403) {
        const pin = window.prompt("این ماه تاییدشده است. کد مدیریتی:");
        if (!pin) return;
        try {
          await updateAttendancePin(editRec.id, payload, pin);
          message.success("رکورد ویرایش شد");
          setEditRec(null);
          loadRecords();
        } catch (e2) { message.error(e2?.response?.data?.detail || "کد مدیریتی نادرست است"); }
      } else { message.error("خطا در ویرایش"); }
    }
  };

  // افزودنِ دستیِ یک رکورد حضور (همان upsertِ manual-day؛ اگر نبود می‌سازد)
  const openAdd = () => {
    addForm.resetFields();
    addForm.setFieldsValue({ work_date: todayISO(), check_in: null, check_out: null });
    setAddOpen(true);
  };
  const submitAdd = async (pin) => {
    const v = await addForm.validateFields();
    const payload = {
      employee_id: v.employee_id,
      work_date: v.work_date || todayISO(),
      check_in: v.check_in ? v.check_in.format("HH:mm:00") : null,
      check_out: v.check_out ? v.check_out.format("HH:mm:00") : null,
      note: v.note || null,
    };
    try {
      await manualCorrectDay(payload, pin);
      message.success("رکورد دستی ثبت شد");
      setAddOpen(false); addForm.resetFields(); loadRecords();
    } catch (e) {
      if (e?.response?.status === 403 && !pin) {
        const p = window.prompt("این ماه نهایی شده است. کد مدیریتی:");
        if (!p) return;
        return submitAdd(p);
      }
      const d = e?.response?.data?.detail;
      message.error(typeof d === "string" ? d : (d?.message || "خطا در ثبت رکورد"));
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteAttendancePin(id);
      message.success("رکورد حذف شد");
      loadRecords();
    } catch (e) {
      if (e?.response?.status === 403) {
        const pin = window.prompt("این ماه تاییدشده است. کد مدیریتی:");
        if (!pin) return;
        try {
          await deleteAttendancePin(id, pin);
          message.success("رکورد حذف شد");
          loadRecords();
        } catch (e2) { message.error(e2?.response?.data?.detail || "کد مدیریتی نادرست است"); }
      } else { message.error("خطا در حذف"); }
    }
  };

  const columns = [
    { title: "پرسنل", dataIndex: "employee_id", width: 180, sorter: byStr("employee_id"),
      render: (id) => empMap[id] || `ID:${id}` },
    { title: "تاریخ", dataIndex: "work_date", width: 120, sorter: byDate("work_date"),
      render: (v) => toJalali(v) },
    { title: "ورود", dataIndex: "check_in", width: 90, sorter: byTime("check_in") },
    { title: "خروج", dataIndex: "check_out", width: 90, sorter: byTime("check_out"),
      render: (v, rec) => v || (rec.note ? <Tag color="orange">ناقص</Tag> : "—") },
    { title: "دقیقه کارکرد", dataIndex: "worked_minutes", width: 130, sorter: byNum("worked_minutes"),
      render: (v) => v ? formatDurationFa(v) : "—" },
    { title: "کسرکار", dataIndex: "shortage_minutes", width: 110, sorter: byNum("shortage_minutes"),
      render: (v) => v > 0 ? <span style={{ color: "red" }}>{formatDurationFa(v)}</span> : "—" },
    { title: "اضافه‌کاری", dataIndex: "overtime_minutes", width: 110, sorter: byNum("overtime_minutes"),
      render: (v) => v > 0 ? <span style={{ color: "green" }}>{formatDurationFa(v)}</span> : "—" },
    // وضعیت از روی ورود/خروج محاسبه می‌شود: هر دو=حاضر، یکی=ناقص، هیچ=غایب (نه status قدیمیِ DB)
    { title: "وضعیت", width: 90,
      render: (_, rec) => {
        const hasIn = !!rec.check_in, hasOut = !!rec.check_out;
        if (hasIn && hasOut) return <Tag color="green">حاضر</Tag>;
        if (hasIn || hasOut) return <Tag color="orange">ناقص</Tag>;
        return <Tag color="red">غایب</Tag>;
      } },
    {
      title: "عملیات", width: 100,
      render: (_, rec) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(rec)} />
          <Popconfirm title="حذف شود؟" onConfirm={() => handleDelete(rec.id)} okText="بله" cancelText="خیر">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const rawColumns = [
    { title: "شناسه دستگاه", dataIndex: "device_uid", width: 110, sorter: byNum("device_uid"),
      render: (v) => <Tag>{v}</Tag> },
    { title: "پرسنل", dataIndex: "employee_name", sorter: byStr("employee_name"),
      render: (v) => v || <Tag color="orange">بدون پرسنل</Tag> },
    { title: "تاریخ", dataIndex: "work_date", width: 120, sorter: byDate("work_date"),
      render: (v) => toJalali(v) },
    { title: "ساعت تردد", dataIndex: "punch_time", width: 100, sorter: byTime("punch_time") },
    { title: "فایل منبع", dataIndex: "source_file", render: (v) => v || "—" },
  ];

  /**
   * کسانی که این ماه ترددی ندارند اولِ فهرست می‌آیند — شناسهٔ بی‌صاحب تقریباً
   * همیشه مالِ یکی از همین‌هاست.
   */
  const mapCandidates = useMemo(() => {
    const gap = new Set((missing?.rows || []).map((r) => r.employee_id));
    const tagged = employees.map((e) => ({ ...e, __missing: gap.has(e.id) }));
    return [...tagged.filter((e) => e.__missing), ...tagged.filter((e) => !e.__missing)];
  }, [employees, missing]);

  const unmappedColumns = [
    { title: "شناسه دستگاه", dataIndex: "device_uid", width: 110,
      render: (v) => <Tag color="orange" style={{ fontSize: 14 }}>{v}</Tag> },
    { title: "نام (حدسی)", width: 200,
      render: (_, rec) => (
        <Space size={4}>
          <span style={{ color: rec.label ? undefined : "#aaa" }}>
            {rec.label || rec.suggested_name || "— بدون نام —"}
          </span>
          {rec.suggested_name && !rec.label &&
            <Tag color="blue" style={{ fontSize: 10 }}>پیشنهادی</Tag>}
          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openLabel(rec)} />
        </Space>
      ) },
    { title: "تعداد تردد", dataIndex: "punch_count", width: 100, align: "center",
      render: (v) => <b>{v}</b> },
    { title: "ماه‌ها", dataIndex: "months", render: (v) => (v || []).join("، ") },
    {
      title: "عملیات", width: 160,
      render: (_, rec) => (
        <Button type="primary" size="small" icon={<UserAddOutlined />}
          onClick={() => { setMapModal({ open: true, device_uid: rec.device_uid }); setMapEmpId(null); }}>
          نگاشت به پرسنل
        </Button>
      ),
    },
  ];

  const periodSelector = (
    <Space>
      <Select value={year} onChange={setYear} style={{ width: 90 }}>
        {JALALI_YEARS.map(y => <Option key={y} value={y}>{y}</Option>)}
      </Select>
      <Select value={month} onChange={setMonth} style={{ width: 100 }}>
        {JALALI_MONTHS.map((m, i) => <Option key={i + 1} value={i + 1}>{m}</Option>)}
      </Select>
    </Space>
  );

  const ROSTER_ACTION = {
    assign: { l: "نگاشت جدید", c: "green" },
    reassign: { l: "تخصیص مجدد", c: "orange" },
    unchanged: { l: "بدون تغییر", c: "default" },
    create: { l: "پرسنل جدید ساخته می‌شود", c: "purple" },
    no_match: { l: "پرسنلِ منطبق نیست", c: "red" },
    ambiguous: { l: "هم‌نامِ مبهم", c: "volcano" },
  };
  const rosterColumns = [
    { title: "کد کارت", dataIndex: "device_code", width: 90, sorter: byStr("device_code") },
    { title: "کد پرسنلی", dataIndex: "personnel_code", width: 100 },
    { title: "نام در دستگاه", dataIndex: "device_name", width: 170 },
    { title: "تردد", dataIndex: "punches", width: 70, align: "center", sorter: byNum("punches") },
    { title: "بازه", width: 170,
      render: (_, r) => (r.first_seen ? `${toJalali(r.first_seen)} تا ${toJalali(r.last_seen)}` : "—") },
    { title: "پرسنل سیستم", width: 260,
      render: (_, r) => (
        <Space size={4} direction="vertical" style={{ width: "100%" }}>
          <Select size="small" showSearch allowClear style={{ width: 240 }}
            placeholder="انتخاب پرسنل…" optionFilterProp="label"
            value={r.employee_id || undefined}
            onChange={(v) => setRosterEmployee(r.device_code, v)}
            options={employees.map((e) => ({ value: e.id, label: `${e.full_name} (${e.employee_code})` }))} />
          {r.match && <Text type="secondary" style={{ fontSize: 11 }}>
            تطبیق: {{ exact: "نام دقیق", fuzzy: "نام نزدیک", manual: "دستی" }[r.match] || r.match}
          </Text>}
          {r.can_create && (
            <Space size={4}>
              <Switch size="small" checked={rosterCreate.includes(r.device_code)}
                onChange={(on) => toggleRosterCreate(r.device_code, on)} />
              <Text style={{ fontSize: 11 }}>پرسنل جدید با همین نام ساخته شود</Text>
            </Space>
          )}
        </Space>
      ) },
    { title: "وضعیت", dataIndex: "action", width: 190,
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <Tag color={ROSTER_ACTION[v]?.c}>{ROSTER_ACTION[v]?.l || v}</Tag>
          {r.previous_owner && (
            <Text type="warning" style={{ fontSize: 11 }}>
              کد از «{r.previous_owner.full_name}» گرفته می‌شود
            </Text>
          )}
        </Space>
      ) },
  ];

  const LEVEL_META = {
    ok: { color: "success", label: "سالم" },
    warning: { color: "warning", label: "نیازمند بررسی" },
    critical: { color: "error", label: "بحرانی" },
  };
  // ---- گزارشِ دوره‌ای: ستون‌ها دقیقاً هم‌نامِ فرمِ چاپیِ دستگاه ----
  const reportColumns = [
    { title: "کارت", dataIndex: "device_user_id", width: 70, align: "center",
      sorter: byNum("device_user_id"), render: (v) => (v == null ? "—" : fa(v)) },
    { title: "پرسنلی", dataIndex: "employee_code", width: 90, sorter: byStr("employee_code"),
      render: (v) => v || "—" },
    { title: "نام و نام خانوادگی", dataIndex: "employee_name", width: 190, fixed: "right",
      sorter: byStr("employee_name"),
      render: (v, r) => (
        <a onClick={() => togglePerson(r.employee_id)} style={{ fontWeight: 600 }}>
          {openEmp === r.employee_id ? "● " : ""}{v}
        </a>
      ) },
    { title: "حضور", dataIndex: "present_minutes", width: 95, align: "center",
      sorter: byNum("present_minutes"), render: (v) => <b>{hhmm(v)}</b> },
    { title: "دیرکرد", dataIndex: "late_minutes", width: 90, align: "center",
      sorter: byNum("late_minutes"),
      render: (v) => (v ? <Text type="warning">{hhmm(v)}</Text> : hhmm(0)) },
    { title: "تعجیل", dataIndex: "early_exit_minutes", width: 90, align: "center",
      sorter: byNum("early_exit_minutes"),
      render: (v) => (v ? <Text type="warning">{hhmm(v)}</Text> : hhmm(0)) },
    // کسرکار به دقیقه و ساعت (خواستهٔ کاربر ۱۴۰۵/۰۷/۰۱)
    { title: "کسر کار", dataIndex: "shortage_minutes", width: 150, align: "center",
      sorter: byNum("shortage_minutes"),
      render: (v) => (v ? <b style={{ color: "var(--gx-danger, #cf1322)" }}>{minutesLabel(v)}</b> : "—") },
    { title: "بخشیده‌شده", dataIndex: "forgiven_minutes", width: 105, align: "center",
      sorter: byNum("forgiven_minutes"),
      render: (v) => (v ? <Tag color="green">{hhmm(v)}</Tag> : "—") },
    // همان قاعده‌ای که موتورِ حقوق اجرا می‌کند: هر N ساعت کسری = یک روزِ حقوقی.
    // بدونِ این ستون، کاربر باید دقیقه‌ها را در ذهن به روز تبدیل کند.
    { title: (
        <Tooltip title={`کسرکار ÷ ${fa(baseHours)} ساعت (مبنای روزِ حقوقی). هر ${fa(baseHours * 60)} دقیقه کسرکار = یک روز حقوق؛ همین عدد × حقوقِ روزانه کسر می‌شود.`}>
          <span style={{ borderBottom: "1px dotted #999" }}>معادلِ روزِ کسرکار</span>
        </Tooltip>),
      dataIndex: "shortage_equiv_days", key: "shortage_equiv_days", width: 120, align: "center",
      sorter: byNum("shortage_equiv_days"),
      render: (v) => (v
        ? <b style={{ color: "var(--gx-danger, #cf1322)" }}>{fa(Number(v).toFixed(2))}</b>
        : "—") },
    { title: "کارکرد", dataIndex: "worked_days", width: 80, align: "center",
      sorter: byNum("worked_days"), render: (v) => <b>{fa(v)}</b> },
    { title: "غیبت", dataIndex: "absent_days", width: 75, align: "center",
      sorter: byNum("absent_days"),
      render: (v) => (v ? <Tag color="red">{fa(v)}</Tag> : fa(0)) },
    { title: "ناقص", dataIndex: "incomplete_days", width: 70, align: "center",
      sorter: byNum("incomplete_days"),
      render: (v) => (v ? <Tag color="warning">{fa(v)}</Tag> : "—") },
    { title: "۳+ تردد", dataIndex: "multi_punch_days", width: 78, align: "center",
      sorter: byNum("multi_punch_days"),
      render: (v) => (v ? <Tag color="orange">{fa(v)}</Tag> : "—") },
    { title: "جریمهٔ تعطیل", dataIndex: "holiday_penalty_days", width: 100, align: "center",
      sorter: byNum("holiday_penalty_days"),
      render: (v) => (v ? <Tag color="volcano">{fa(v)}</Tag> : "—") },
    { title: "باقی‌ماندهٔ بررسی", dataIndex: "unresolved_count", width: 130, align: "center",
      sorter: byNum("unresolved_count"),
      render: (v, r) => (v
        ? <a onClick={() => togglePerson(r.employee_id)}><Tag color="orange">{fa(v)} روز</Tag></a>
        : <Tag color="green">تمام</Tag>) },
  ];

  /* ستون‌های خواسته‌شدهٔ کاربر: کارت و کدِ پرسنلی، مجموعِ ساعتِ حضور، دیرکرد،
     تعجیل، کسرکار (دقیقه و معادلِ روز)، اضافه‌کاری، تعطیلی، غیبت. */
  const summaryColumns = [
    { title: "کارت", dataIndex: "device_user_id", width: 64, align: "center",
      sorter: byNum("device_user_id"), render: (v) => (v == null ? "—" : fa(v)) },
    { title: "کد پرسنلی", dataIndex: "employee_code", width: 90,
      sorter: byStr("employee_code"), render: (v) => (v ? fa(v) : "—") },
    { title: "پرسنل", dataIndex: "employee_name", width: 150,
      render: (v, r) => <span><a onClick={() => openPerson(r.employee_id)}>{v}</a><NewStaffTag rec={r} /></span> },
    { title: "مدل", dataIndex: "pay_model", width: 70,
      render: v => ({ fixed: "ثابت", daily: "ثابت", piece: "کارمزد", none: "بدون پایه" }[v] || v) },
    { title: "جمع حضور", dataIndex: "worked_minutes", width: 100, align: "center",
      sorter: byNum("worked_minutes"), render: (v) => hhmm(v) },
    { title: "دیرکرد", dataIndex: "late_minutes", width: 85, align: "center",
      sorter: byNum("late_minutes"),
      render: (v) => (v ? <Text type="warning">{hhmm(v)}</Text> : "—") },
    { title: "تعجیل", dataIndex: "early_exit_minutes", width: 85, align: "center",
      sorter: byNum("early_exit_minutes"),
      render: (v) => (v ? <Text type="warning">{hhmm(v)}</Text> : "—") },
    { title: "کسرکار (روز)", dataIndex: "shortage_equiv_days", width: 100, align: "center",
      sorter: byNum("shortage_equiv_days"),
      render: (v, r) => (v
        ? <Tooltip title={`${hhmm(r.total_shortage_minutes)} کسری`}>
          <b style={{ color: "#cf1322" }}>{fa(v)}</b></Tooltip>
        : "—") },
    { title: "اضافه‌کاری", dataIndex: "overtime_minutes", width: 95, align: "center",
      sorter: byNum("overtime_minutes"),
      render: (v) => (v ? <Tag color="green">{hhmm(v)}</Tag> : "—") },
    { title: "تعطیلی", dataIndex: "holiday_days", width: 75, align: "center",
      render: (v) => fa(v || 0) },
    { title: "حضور", dataIndex: "present_days", width: 70, align: "center" },
    { title: "غیبت", dataIndex: "absent_days", width: 70, align: "center",
      render: v => v ? <span style={{ color: "#cf1322" }}>{v}</span> : v },
    { title: "ناقص", dataIndex: "incomplete_days", width: 70, align: "center",
      render: v => v ? <span style={{ color: "#d46b08" }}>{v}</span> : v },
    { title: "چندپانچ", dataIndex: "multi_punch_days", width: 80, align: "center" },
    { title: "کسری", dataIndex: "shortage_days", width: 70, align: "center",
      render: (v, r) => v ? `${v} (${formatDurationFa(r.total_shortage_minutes)})` : "—" },
    { title: "جریمهٔ تعطیل", dataIndex: "holiday_penalty_days", width: 90, align: "center" },
    { title: "بررسی", width: 90, align: "center",
      render: (_, r) => `${r.reviewed_count}/${r.needs_review_count}` },
    { title: "وضعیت", dataIndex: "main_issue_level", width: 120,
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <Tag color={LEVEL_META[v].color}>{LEVEL_META[v].label}</Tag>
          {r.finalizable && <Tag color="green" style={{ fontSize: 10 }}>قابل‌نهایی‌سازی</Tag>}
        </Space>
      ) },
    { title: "شرح", dataIndex: "issues", ellipsis: true },
  ];

  /* ── لیستِ نفراتِ ماه ───────────────────────────────────────────────
     یک لیست به‌جای دو تا. «روزهای هر نفر» و «کسرکار و کارکرد» یک دادهٔ
     یکسان را دو جور نشان می‌دادند و هیچ‌کدام کامل نبود؛ کاردکس هم تبِ سومی
     بود که باید نفر را از یک منوی دیگر انتخاب می‌کردی. حالا هر نفر یک ردیف
     است و با کلیک روی نامش کاردکسِ کاملِ ماهش زیرِ همان ردیف باز می‌شود —
     همان لیست در مرحلهٔ ۴ و مرحلهٔ ۵ (تأیید نهایی) استفاده می‌شود. */
  const peopleRows = useMemo(() => {
    let rows = reportData?.rows || [];
    if (onlyProblems) rows = rows.filter((r) => r.unresolved_count > 0);
    const q = peopleSearch.trim();
    if (q) rows = rows.filter((r) =>
      (r.employee_name || "").includes(q)
      || String(r.employee_code || "").includes(q)
      || String(r.device_user_id ?? "").includes(q));
    return rows;
  }, [reportData, peopleSearch, onlyProblems]);

  const money0 = (v) => fa(Number(v || 0).toFixed(2));
  /* ── کاردکسِ نفرِ انتخاب‌شده، **بالای** لیست (۱۴۰۵/۰۷/۰۱) ──
     قبلاً زیرِ ردیفِ خودش باز می‌شد و لیست را هل می‌داد پایین. حالا لیست جداست و
     کاردکس بالای آن؛ با «قبلی/بعدی» بینِ نفراتِ همین لیست (با همان فیلتر) می‌رویم. */
  const openRow = (reportData?.rows || []).find((r) => r.employee_id === openEmp);
  const openIdx = peopleRows.findIndex((r) => r.employee_id === openEmp);
  const stepPerson = (dir) => {
    const next = peopleRows[openIdx + dir];
    if (next) setOpenEmp(next.employee_id);
  };
  const personDetail = (
    <div ref={personTopRef} style={{ marginBottom: 14 }}>
      {openEmp == null ? (
        <div style={{ border: "1px dashed var(--gx-border, #ddd)", borderRadius: 8, padding: 14,
                      textAlign: "center", color: "#999" }}>
          روی نامِ یک نفر در لیستِ پایین بزنید تا روزهای ماهش (هفته‌به‌هفته) اینجا باز شود.
        </div>
      ) : (
        <Card size="small"
          title={<Space wrap>
            <b>{openRow?.employee_name || "…"}</b>
            {openRow?.employee_code && <Tag>{fa(openRow.employee_code)}</Tag>}
            <Text type="secondary" style={{ fontSize: 12 }}>
              {JALALI_MONTHS[month - 1]} {fa(year)}
            </Text>
          </Space>}
          extra={<Space size={4}>
            <Button size="small" disabled={openIdx <= 0} onClick={() => stepPerson(-1)}>قبلی</Button>
            <Button size="small" disabled={openIdx < 0 || openIdx >= peopleRows.length - 1}
              onClick={() => stepPerson(1)}>بعدی</Button>
            <Button size="small" onClick={() => setOpenEmp(null)}>بستن</Button>
          </Space>}>
          <PersonDaysPanel employeeId={openEmp} employeeName={openRow?.employee_name}
            year={year} month={month} baseHours={baseHours}
            locked={monthStatus?.status === "finalized"}
            onChanged={loadPeriodReport} />
        </Card>
      )}
    </div>
  );

  const peopleList = (
    <>
      {personDetail}
      <Space style={{ marginBottom: 10 }} wrap>
        <Input.Search allowClear placeholder="جستجوی نام، کدِ پرسنلی یا کارت…"
          value={peopleSearch} onChange={(e) => setPeopleSearch(e.target.value)}
          style={{ width: 280 }} />
        <Space size={4}>
          <Switch size="small" checked={onlyProblems} onChange={setOnlyProblems} />
          <Text style={{ fontSize: 12 }}>فقط کسانی که کارِ رسیدگی‌نشده دارند</Text>
        </Space>
        <Space size={4}>
          <Switch size="small" checked={reportAll} onChange={setReportAll} />
          <Text style={{ fontSize: 12 }}>شاملِ کارمزدی‌ها</Text>
        </Space>
        <Button type="primary" onClick={loadPeople}>نمایش</Button>
        <ExportButton rows={peopleRows} columns={reportColumns}
          filename={`کارکرد-و-کسرکار-${year}-${month}`} />
        <Text type="secondary" style={{ fontSize: 12 }}>{fa(peopleRows.length)} نفر</Text>
      </Space>
      <GxTable layoutId="attendance-people" dataSource={peopleRows} columns={reportColumns}
        rowKey="employee_id" size="small" loading={reportLoading}
        scroll={{ x: "max-content" }}
        rowClassName={(r) => [r.unresolved_count ? "gx-op-warn" : "",
          r.employee_id === openEmp ? "att-row-selected" : ""].join(" ")}
        onRow={(r) => ({ onClick: (e) => {
          // کلیک روی کنترل‌های داخلِ ردیف (لینک/دکمه) خودش کار می‌کند
          if (e.target.closest("a,button,.ant-select")) return;
          setOpenEmp(r.employee_id);
          setTimeout(() => personTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
        }, style: { cursor: "pointer" } })}
        gxSummary={[
          { dataIndex: "present_minutes", type: "sum", format: hhmm },
          { dataIndex: "late_minutes", type: "sum", format: hhmm },
          { dataIndex: "early_exit_minutes", type: "sum", format: hhmm },
          { dataIndex: "shortage_minutes", type: "sum", format: minutesLabel },
          { dataIndex: "forgiven_minutes", type: "sum", format: hhmm },
          { dataIndex: "shortage_equiv_days", type: "sum", format: money0 },
          { dataIndex: "worked_days", type: "sum", format: fa },
          { dataIndex: "absent_days", type: "sum", format: fa },
          { dataIndex: "incomplete_days", type: "sum", format: fa },
          { dataIndex: "multi_punch_days", type: "sum", format: fa },
          { dataIndex: "holiday_penalty_days", type: "sum", format: fa },
          { dataIndex: "unresolved_count", type: "sum", format: fa },
        ]} />
    </>
  );

  // کارتِ ورود/خروجِ اطلاعات — همیشه بالای صفحهٔ حضور (نه داخل تب)
  const importCard = (
    <Card
      title={`ورود/خروج اطلاعات حضور و غیاب — ${JALALI_MONTHS[month - 1]} ${fa(year)}`}
      style={{ marginBottom: 16 }}
      extra={monthData && (
        <Space size={6} wrap>
          <Tag color={monthData.punches ? "blue" : "default"}>
            پانچ: {fa(monthData.punches || 0)}
          </Tag>
          <Tag>روز: {fa(monthData.days || 0)}</Tag>
          <Tag>پرسنل: {fa(monthData.employees || 0)}</Tag>
          {!!monthData.manual_days &&
            <Tag color="purple">اصلاحِ دستی: {fa(monthData.manual_days)}</Tag>}
          <Popconfirm
            title={`حذفِ دادهٔ حضورِ ${JALALI_MONTHS[month - 1]} ${fa(year)}`}
            description={
              <div style={{ maxWidth: 300 }}>
                همهٔ پانچ‌ها و روزهای این ماه پاک می‌شوند تا ایمپورتِ دوباره
                تجمیع نشود. {monthData.manual_days
                  ? `${fa(monthData.manual_days)} روزِ اصلاح‌شدهٔ دستی حفظ می‌شود.`
                  : ""}
              </div>}
            okText="حذف کن" cancelText="انصراف" okButtonProps={{ danger: true }}
            disabled={!monthData.punches && !monthData.days}
            onConfirm={() => doDeleteMonth(true)}
          >
            <Button danger size="small" icon={<DeleteOutlined />}
              disabled={!monthData.punches && !monthData.days}>
              حذف دادهٔ این ماه
            </Button>
          </Popconfirm>
        </Space>
      )}
    >
      <GxNote id="att-scope" signature={`${year}-${month}`}
        message={<>فقط ترددِ <b>{JALALI_MONTHS[month - 1]} {fa(year)}</b> وارد می‌شود — ماه را از بالای صفحه عوض کنید.</>} />
      {missing?.count > 0 && (
        <GxNote id="att-missing" type="warning"
          signature={`${year}-${month}-${missing.count}`}
          message={<>{fa(missing.count)} نفر از پرسنلِ حضوری در حضور و غیابِ این ماه نیستند</>}>
          {/* همهٔ اسم‌ها اینجاست؛ بریده نمی‌شود، فقط تا باز نکنی دیده نمی‌شود */}
          <Space direction="vertical" size={0} style={{ width: "100%" }}>
            {missing.rows.map((r) => (
              <span key={r.employee_id}>
                {r.full_name} ({fa(r.employee_code)}) — {r.reason}
              </span>
            ))}
          </Space>
        </GxNote>
      )}
      {/* ترددی که در فایل هست ولی به پرسنلِ غیرفعال/غیرحضوری رسیده ⇒ در لیست دیده نمی‌شود (v2.110.0) */}
      {missing?.hidden_count > 0 && (
        <GxNote id="att-hidden" type="warning"
          signature={`${year}-${month}-h${missing.hidden_count}`}
          message={<>{fa(missing.hidden_count)} نفر در فایلِ دستگاه تردد دارند ولی در لیستِ حضور نمی‌آیند</>}>
          <Space direction="vertical" size={0} style={{ width: "100%" }}>
            {missing.hidden.map((r) => (
              <span key={`${r.employee_id}-${r.device_uid}`}>
                {r.full_name} ({fa(r.employee_code || "بی‌کد")}) — کارتِ {fa(r.device_uid)}، {fa(r.punches)} تردد — {r.reason}
              </span>
            ))}
          </Space>
          <div style={{ fontSize: 11, marginTop: 6, opacity: 0.8 }}>
            اگر باید در حضور و غیاب باشند: در «پرسنل» تیکِ «حضور و غیاب دستگاه» را بزنید یا پرونده‌های تکراری را ادغام کنید؛
            بعد فایل را دوباره بارگذاری کنید (ویرایش‌های دستی حفظ می‌شوند). یا در «ثبتِ دستیِ ماه» برایشان ثبت کنید.
          </div>
        </GxNote>
      )}
      <ImportExportBar
        templateUrl="/api/v1/templates/attendance"
        templateLabel="حضور و غیاب"
        onImport={(file) => handleImport({ file })}
        loading={loading}
        accept=".dat,.txt,.csv,.xlsx"
        result={importResult ? { message: importResult.message } : null}
        exportUrl="/api/v1/export/attendance"
        exportParams={{ year, month }}
        exportLabel="خروجی کارکرد ماه"
      />
      {importResult && (importResult.unmapped_device_ids?.length > 0) && (
        <GxNote id="att-unknown-dev" type="warning" style={{ marginTop: 12 }}
          signature={String(importResult.unknown_device_records_preserved)}
          message={<>{fa(importResult.unknown_device_records_preserved)} ثبتِ تردد از شناسه‌های بدون پرسنل حفظ شد</>}>
          این ترددها گم نشده‌اند. در تبِ «شناسه‌های بی‌صاحب» می‌توانید نام بگذارید
          یا آن‌ها را به پرسنل نگاشت کنید.
        </GxNote>
      )}
    </Card>
  );

  // پیش‌نمایشِ ایمپورت حضور (الگوی نرخنامه): تا تأیید نشود چیزی در DB نوشته نمی‌شود
  const previewActionTag = {
    new: { l: "رکورد جدید", c: "green" },
    duplicate: { l: "تکراری — رد می‌شود", c: "default" },
    skipped_manual: { l: "دستی — دست‌نخورده", c: "blue" },
    unmapped: { l: "بدون پرسنل — فقط پانچ خام", c: "orange" },
    conflict: { l: "شناسهٔ تکراریِ یک پرسنل — رد می‌شود", c: "red" },
    not_found: { l: "کد پرسنلی ناشناس", c: "red" },
  };

  const previewColumns = [
    { title: "شناسه دستگاه", dataIndex: "device_uid", width: 100, sorter: byNum("device_uid") },
    { title: "پرسنل", dataIndex: "employee_name", width: 180,
      render: (v, r) => v ? `${v} (${r.employee_code})` : <Text type="secondary">—</Text> },
    { title: "تاریخ", dataIndex: "work_date", width: 110, sorter: byDate("work_date"),
      render: (v) => toJalali(v) },
    { title: "ماه", dataIndex: "jalali_month", width: 110 },
    { title: "ورود", dataIndex: "check_in", width: 90 },
    { title: "خروج", dataIndex: "check_out", width: 90, render: (v) => v || <Text type="secondary">—</Text> },
    { title: "کارکرد", dataIndex: "worked_minutes", width: 100, sorter: byNum("worked_minutes"),
      render: (v) => formatDurationFa(v) },
    { title: "کسری", dataIndex: "shortage_minutes", width: 100, sorter: byNum("shortage_minutes"),
      render: (v) => v ? <Text type="danger">{formatDurationFa(v)}</Text> : "—" },
    { title: "وضعیت ثبت", dataIndex: "action", width: 170,
      render: (v, r) => (
        <Space size={4}>
          <Tag color={previewActionTag[v]?.c}>{previewActionTag[v]?.l || v}</Tag>
          {r.is_incomplete && <Tag color="orange">ناقص</Tag>}
        </Space>
      ) },
  ];

  const previewModal = (
    <Modal
      title="پیش‌نمایش ایمپورت حضور و غیاب — تا تأیید نکنید چیزی ذخیره نمی‌شود"
      open={!!preview}
      onOk={confirmImport}
      onCancel={() => setPreview(null)}
      okText="تأیید و ثبت" cancelText="انصراف"
      confirmLoading={applying}
      width={1100}
      okButtonProps={{ disabled: !preview?.data?.rows?.length }}
    >
      {preview && (
        <>
          <Alert
            type="info" showIcon style={{ marginBottom: 12 }}
            message={`فایل ${preview.data.file_type === "dat" ? "دستگاه (DAT)" : "متنی (TXT)"} — ${faNum(preview.data.punches_in_file)} ثبت خوانده شد`}
            description={
              <span style={{ fontSize: 12 }}>
                ماه‌های شناسایی‌شده: <b>{preview.data.parsed_months.join("، ") || "—"}</b>
                {preview.data.file_type === "dat" && <>
                  {" | "}پانچ خامِ جدید: <b>{faNum(preview.data.raw_punches_new)}</b>
                  {" | "}پانچ تکراری: <b>{faNum(preview.data.raw_punches_duplicate)}</b>
                </>}
              </span>
            }
          />
          <Space wrap style={{ marginBottom: 12 }}>
            <Tag color="green">رکورد جدید: {faNum(preview.data.summary.new || 0)}</Tag>
            <Tag>تکراری: {faNum(preview.data.summary.duplicate || 0)}</Tag>
            <Tag color="blue">دستی (دست‌نخورده): {faNum(preview.data.summary.skipped_manual || 0)}</Tag>
            {preview.data.summary.unmapped > 0 &&
              <Tag color="orange">بدون پرسنل: {faNum(preview.data.summary.unmapped)}</Tag>}
            {preview.data.summary.not_found > 0 &&
              <Tag color="red">کد ناشناس: {faNum(preview.data.summary.not_found)}</Tag>}
            {preview.data.summary.conflict > 0 &&
              <Tag color="red">شناسهٔ تکراری: {faNum(preview.data.summary.conflict)}</Tag>}
          </Space>
          {preview.data.unmapped_devices?.length > 0 && (
            <Alert
              type="warning" showIcon style={{ marginBottom: 12 }}
              message={`${faNum(preview.data.unmapped_devices.length)} شناسهٔ دستگاه به هیچ پرسنلی نگاشت نشده — پانچ‌هایشان حفظ می‌شود ولی رکورد کارکرد ساخته نمی‌شود`}
              description={
                <span style={{ fontSize: 12 }}>
                  {preview.data.unmapped_devices.map((d) =>
                    `${d.device_uid}${d.label || d.suggested_name ? ` (${d.label || d.suggested_name})` : ""} — ${d.punch_count} پانچ`
                  ).join("  |  ")}
                </span>
              }
            />
          )}
          <Table
            size="small" rowKey={(r, i) => `${r.device_uid || r.employee_code}-${r.work_date}-${i}`}
            columns={previewColumns} dataSource={preview.data.rows}
            pagination={{ pageSize: 12, showSizeChanger: false }}
            scroll={{ x: "max-content" }}
          />
        </>
      )}
    </Modal>
  );

  const tabs = [
    // مرحلهٔ ۱ — همان کارتِ ایمپورت، حالا داخلِ مرحله (نه همیشه بالای صفحه)
    { key: "import", label: "بارگذاری فایل", children: importCard },
    {
      key: "records",
      label: "رکوردهای حضور",
      children: (
        <>
          <Card
            title="مشاهده کارکرد"
            extra={
              <Space wrap>
                    <Button type="primary" onClick={loadRecords}>نمایش</Button>
                <Button icon={<UserAddOutlined />} onClick={openAdd}>افزودن دستی</Button>
                <ExportButton url="/api/v1/export/attendance" params={{ year, month }} label="کارکرد ماه" />
              </Space>
            }
          >
            <GxTable dataSource={records} columns={columns} rowKey="id" size="small"
              loading={loading} pagination={{ pageSize: 30 }} scroll={{ x: "max-content" }} />
          </Card>
        </>
      ),
    },
    {
      key: "people",
      label: (
        <Badge count={unmapped.length} size="small" offset={[10, -2]}>
          <span>کارت‌های دستگاه</span>
        </Badge>
      ),
      children: (
        <>
        <DeviceCardsTab year={year} month={month} employees={employees}
          onChanged={() => { loadUnmapped(); loadPeriodReport(); }} />
        <Card style={{ marginTop: 12 }}
          title="برچسب و نگاشتِ شناسه‌های بی‌صاحب"
          extra={<Button icon={<ReloadOutlined />} size="small" onClick={loadUnmapped}>تازه‌سازی</Button>}
        >
          <Alert type="info" showIcon style={{ marginBottom: 12 }}
            message="این‌ها کسانی هستند که در دستگاه تردد زده‌اند ولی در سیستم به‌عنوان پرسنل ثبت نشده‌اند (مثلاً نیروی جدید، یا کسی که اخراج شده و کدش خالی شده ولی ترددهایش مانده). ترددشان حفظ شده است."
            description="با دکمهٔ مدادِ ستون «نام»، یک نام حدسی کنار شناسه بگذارید تا بعداً بشناسیدش. با «نگاشت به پرسنل»، ترددها به یک پرسنلِ موجود وصل و رکوردهای حضور بدون آپلود مجدد ساخته می‌شوند." />
          <GxTable dataSource={unmapped} columns={unmappedColumns} rowKey="device_uid"
            size="small" pagination={false} />
          {/* نگاشتِ انبوه از فایلِ خروجیِ دستگاه — کمتر لازم می‌شود، پس جمع‌شده */}
          <Collapse size="small" style={{ marginTop: 12 }} items={[{
            key: "roster",
            label: "نگاشتِ دسته‌جمعی از فایلِ خروجیِ نام‌دارِ دستگاه",
            children: (
              <Card
          title="نگاشت کدِ کارتِ دستگاه (۴رقمی) و کدِ پرسنلی (۵رقمی) از خروجیِ خودِ دستگاه"
          extra={
            <Space>
              <Upload beforeUpload={(f) => { setRosterOverrides({}); setRosterCreate([]); rosterPreview(f); return false; }}
                showUploadList={false} accept=".txt,.TXT">
                <Button icon={<UserAddOutlined />} loading={rosterLoading}>انتخاب فایل خروجی دستگاه</Button>
              </Upload>
              {roster && <Button type="primary" onClick={rosterApply} loading={rosterLoading}>اعمال نگاشت</Button>}
              {roster && <Button onClick={() => { setRoster(null); setRosterFile(null); }}>انصراف</Button>}
            </Space>
          }
        >
          <Alert type="info" showIcon style={{ marginBottom: 12 }}
            message="فایلِ نام‌دارِ دستگاه (مثل GLG_001.TXT) را بدهید؛ کدِ کارت و نامِ هر نفر از خودِ دستگاه خوانده می‌شود."
            description="کدِ پرسنلی همان کدِ کارت با یک صفرِ بیشتر است (۰۰۰۹ → ۰۰۰۰۹). اگر کدی قبلاً مالِ نفرِ دیگری بوده، از او گرفته و به نفرِ جدید داده می‌شود و هشدارش همین‌جا نشان داده می‌شود. تا «اعمال نگاشت» را نزنید چیزی ذخیره نمی‌شود." />
          {!roster && <div style={{ color: "#999", padding: 20, textAlign: "center" }}>فایل خروجیِ دستگاه را انتخاب کنید.</div>}
          {roster && (
            <>
              <Space wrap style={{ marginBottom: 12 }}>
                <Tag color="blue">{roster.message}</Tag>
                <Tag color="green">نگاشت جدید: {roster.summary.assign}</Tag>
                <Tag color="orange">تخصیص مجدد: {roster.summary.reassign}</Tag>
                <Tag>بدون تغییر: {roster.summary.unchanged}</Tag>
                <Tag color="purple">پرسنل جدید: {roster.summary.create}</Tag>
                <Tag color="red">بی‌تطبیق: {roster.summary.no_match}</Tag>
              </Space>
              <Table size="small" rowKey="device_code" dataSource={roster.rows}
                loading={rosterLoading} pagination={{ pageSize: 20, showSizeChanger: false }}
                scroll={{ x: "max-content" }}
                rowClassName={(r) => (r.action === "reassign" ? "att-row-warn"
                  : (r.action === "no_match" ? "att-row-absent" : ""))}
                columns={rosterColumns} />
            </>
          )}
        </Card>
            ),
          }]} />
        </Card>
        </>
      ),
    },
    {
      key: "raw",
      label: "ثبت‌های خام دستگاه",
      children: (
        <Card
          title="ثبت‌های خام تردد (پانچ دستگاه)"
          extra={
            <Space>
                <Button type="primary" onClick={loadRawPunches}>نمایش</Button>
            </Space>
          }
        >
          <Alert type="info" showIcon style={{ marginBottom: 12 }}
            message="ثبت خام چیست؟"
            description="هر بار که یک نفر در دستگاه حضورغیاب کارت/انگشت می‌زند، یک «ثبتِ خام» (به‌انگلیسی punch) ساخته می‌شود — یعنی یک رویدادِ ورود یا خروج با تاریخ و ساعتِ دقیق. این فهرست همهٔ این ثبت‌ها را همان‌طور که از دستگاه خوانده شده‌اند نشان می‌دهد، حتی قبل از این‌که به رکورد روزانهٔ حضور (ورود/خروج/کارکرد) تبدیل شوند. هدف این است که هیچ ترددی گم نشود و در صورت اشکال بتوان به دادهٔ اصلی رجوع کرد." />
          <GxTable dataSource={rawPunches} columns={rawColumns} rowKey="id" size="small"
            loading={rawLoading} pagination={{ pageSize: 50 }} scroll={{ x: "max-content" }} />
        </Card>
      ),
    },
    {
      key: "review",
      label: "روزهای هر نفر — کسرکار و کارکرد",
      children: (
        <Card
          title={`روزهای هر نفر — ${JALALI_MONTHS[month - 1]} ${fa(year)}`}
          extra={<Space>
            <Button onClick={() => setHelpOpen(true)}>راهنما</Button>
            {monthStatus?.status === "finalized" && <Tag color="green">ماه نهایی شده</Tag>}
          </Space>}
        >
          <GxNote id="att-people-list" signature={`${year}-${month}`}
            message="لیستِ نفرات پایین است — روی هر نفر بزنید تا روزهای ماهش بالای لیست باز شود">
            هر ردیف جمعِ ماهِ یک نفر است (همان فرمِ کاغذیِ دستگاه). با کلیک روی یک نفر،
            <b>روزهای کاملِ ماهش</b> بالای لیست باز می‌شود (هفته‌ها با خطِ پررنگ جدا شده‌اند)
            و با «قبلی/بعدی» بینِ نفرات می‌روید. سه تیکِ بالای آن فقط <b>غیبت‌ها</b>،
            <b>خطای تردد</b> (یک تردد یا ۳+) یا <b>کسری‌دار</b>ها را نشان می‌دهد. در «ورود/خروج»
            ترددهای خامِ دستگاه دیده می‌شوند: دو تا را ورود و خروج کنید، یا ساعتِ جاافتاده را بنویسید.
            ستون‌ها را با کشیدنِ سرستون جابه‌جا کنید؛ چیدمان یادش می‌ماند.
            <br />
            «کسر کار» = دیرکرد + تعجیل (دقیقه و ساعت) و همین در حقوق کسر می‌شود؛ «بخشیده»
            کسریِ زیرِ تلورانسِ روزانه است و حساب نمی‌شود. «معادلِ روزِ کسرکار» = کسرکار ÷
            مبنای روزِ حقوقی (مثلاً هر ۴۲۰ دقیقه = یک روز)؛ همین × حقوقِ روزانه کم می‌شود.
          </GxNote>
          {peopleList}
        </Card>
      ),
    },
    {
      key: "manual",
      label: "ثبتِ دستیِ ماه",
      children: <ManualMonthTab year={year} month={month} />,
    },
    {
      key: "summary",
      label: "خلاصهٔ ماهانه بر اساس نفر",
      children: (
        <Card
          title="خلاصهٔ ماهانهٔ حضور — هر نفر یک ردیف (فقط پرسنلِ «حضوری»؛ کارمزدی/راننده نمی‌آیند)"
          extra={<Button type="primary" onClick={loadSummary}>نمایش</Button>}
        >
          <Alert type="info" showIcon style={{ marginBottom: 12 }}
            message="روی نام هر نفر کلیک کنید تا کاردکس ماهانه‌اش باز شود. رنگ وضعیت: سبز=سالم، نارنجی=نیازمند بررسی، قرمز=بحرانی (غیبتِ حل‌نشده). «قابل‌نهایی‌سازی» یعنی همهٔ موارد نیازمند بررسی، بازبینی شده‌اند." />
          <GxTable dataSource={summaryData?.rows || []} columns={summaryColumns}
            rowKey="employee_id" size="small" loading={summaryLoading}
            pagination={{ pageSize: 50 }} scroll={{ x: "max-content" }} />
        </Card>
      ),
    },
        {
      key: "finalize",
      label: "تأیید نهایی ماه",
      children: (
        <Card title={`تأیید نهاییِ حضورِ ${JALALI_MONTHS[month - 1]} ${fa(year)}`}>
          <GxNote id="att-finalize" signature={`${year}-${month}`}
            message={monthStatus?.status === "finalized"
              ? "این ماه نهایی شده — محاسبهٔ حقوقش باز است"
              : "تا این ماه تأیید نشود، محاسبهٔ حقوقِ آن باز نمی‌شود"}>
            {/* دو عددِ متفاوت روی صفحه هست و بدونِ این توضیح شبیهِ باگ است */}
            «مانعِ نهایی‌سازی» فقط روزهای ناقص، چندپانچ و جریمهٔ مجاورتِ تعطیلی را
            می‌شمارد — همان‌هایی که سیستم تصمیمِ صریح می‌خواهد. غیبت‌ها و کسری‌ها
            در لیستِ پایین دیده می‌شوند ولی جلوی تأیید را نمی‌گیرند، چون خودشان
            تصمیمِ حسابدار نیستند و مستقیم در حقوق اثر می‌کنند.
          </GxNote>

          <Space wrap style={{ marginBottom: 12 }}>
            <Tag color={monthStatus?.status === "finalized" ? "green" : "orange"}>
              وضعیت: {monthStatus?.status === "finalized" ? "نهایی‌شده" : "پیش‌نویس"}
            </Tag>
            <Tag color={monthStatus?.unresolved_issues ? "red" : "green"}>
              مانعِ نهایی‌سازی: {fa(monthStatus?.unresolved_issues ?? 0)}
            </Tag>
            {reportData?.totals && (
              <Tag>کلِ روزهای رسیدگی‌نشده: {fa(reportData.totals.unresolved_count || 0)}</Tag>
            )}
            {monthData && <Tag color="blue">پانچ: {fa(monthData.punches || 0)}</Tag>}
            {monthStatus?.status === "finalized" ? (
              <>
                <Button onClick={() => window.location.assign("/payroll")}>
                  رفتن به محاسبهٔ حقوق
                </Button>
                <Button danger onClick={reopenMonth}>بازگشاییِ ماه (کد مدیریتی)</Button>
              </>
            ) : (
              <Button danger type="primary" onClick={finalizeMonth}>
                تأیید نهاییِ حضورِ ماه
              </Button>
            )}
            <Button onClick={loadPeople}>تازه‌سازی</Button>
          </Space>

          {monthStatus?.status !== "finalized" && monthStatus?.unresolved_issues > 0 && (
            <Alert type="warning" showIcon style={{ marginBottom: 12 }}
              message={`${fa(monthStatus.unresolved_issues)} مورد هنوز تصمیم‌گیری نشده`}
              description="در همین لیست، نامِ هر نفر را باز کنید و تصمیم بگذارید یا «تأیید همهٔ موارد» را بزنید." />
          )}

          {/* همان لیستِ مرحلهٔ ۴ — کاربر می‌خواست اینجا هم بتواند هر نفر را باز
              کند، ورود/خروج را اصلاح کند و تأییدش را همان‌جا بزند. */}
          {peopleList}
        </Card>
      ),
    },
    {
      key: "holidays",
      label: "تعطیلی‌ها و ساعت کاری",
      /* تغییرِ تعطیلی/ساعتِ روز مستقیم روی کسریِ همان ماه اثر می‌گذارد؛
         لیستِ نفرات باید کهنه نماند. */
      children: <AttendanceSettingsTab year={year} month={month}
        onChanged={loadPeople} />,
    },
    {
      key: "weekly",
      label: "گریدِ هفتگی",
      children: (
        <WeeklyGrid year={year} month={month}
          locked={monthStatus?.status === "finalized"} />
      ),
    },
  ];

  const tabContent = Object.fromEntries(tabs.map((t) => [t.key, t.children]));
  // لودرها را افکتِ [year, month, activeTab] صدا می‌زند؛ اینجا فقط تب عوض می‌شود
  const goTab = (k) => { saveOrder("atab", "attendance", k); setActiveTab(k); };

  // ترتیبِ تب‌ها = ترتیبِ واقعیِ کار: فایلِ دستگاه را می‌گیریم ← کدها را نگاشت
  // می‌کنیم ← نفربه‌نفر روزهای مشکل‌دار را رسیدگی می‌کنیم ← گزارشِ دوره‌ای را
  // می‌گیریم و می‌رویم سراغِ حقوق. مرحلهٔ ۲ فقط وقتی شناسهٔ بی‌صاحب هست دیده می‌شود.
  // چهار قدمِ کار، همیشه دیده می‌شوند و شمارنده دارند تا معلوم باشد کجا کار مانده.
  // ابزارهای کم‌کاربرد (ریزِ روزانه، پانچِ خام) زیرِ «بیشتر» می‌مانند.
  /* مراحلِ کار به همان ترتیبی که واقعاً انجام می‌شود — نه فهرستِ امکانات.
     هر مرحله عددِ «چقدر کار مانده» را روی خودش دارد. */
  const MAIN_TABS = [
    { value: "import", label: "۱) بارگذاری فایل" },
    { value: "people",
      label: `۲) کارت‌ها و پرسنل${unmapped.length ? ` (${fa(unmapped.length)})` : ""}` },
    { value: "holidays", label: "۳) تعطیلی‌ها و ساعت کاری" },
    /* «روزهای هر نفر» و «کسرکار و کارکرد» یکی شدند: یک لیست، یک عدد. */
    { value: "review",
      label: `۴) روزها، کسرکار و کارکرد${reportData?.totals?.unresolved_count
        ? ` (${fa(reportData.totals.unresolved_count)})` : ""}` },
    /* ثبتِ دستیِ ماه: وقتی دستگاه نبوده یا فقط برگهٔ کاغذی داریم — یک ردیف per نفر.
       🐞 v2.107.0 فقط به tabContent اضافه شده بود و چون ناوبری از MAIN_TABS/MORE_TABS
       ساخته می‌شود، هیچ‌جا دیده نمی‌شد. */
    { value: "manual", label: "۵) ثبتِ دستیِ ماه" },
    { value: "finalize",
      label: `۶) تأیید نهایی${monthStatus?.unresolved_issues ? ` (${fa(monthStatus.unresolved_issues)})` : ""}` },
  ];
  // ابزارهای کم‌کاربرد — از مسیرِ اصلیِ کار بیرون‌اند تا شلوغش نکنند
  const MORE_TABS = [
    { key: "weekly", label: "گریدِ هفتگی (ویرایشِ انبوه)" },
    { key: "summary", label: "خلاصهٔ ماهانه (وضعیت بررسی)" },
    { key: "records", label: "نمایش ریز روزانه" },
    { key: "raw", label: "ثبت‌های خام دستگاه" },
  ];
  /* کلیدهای قدیمی نباید صفحهٔ خالی بدهند: هم لینکِ ذخیره‌شدهٔ کاربر
     (?tab=shortage) و هم تبِ به‌یادمانده در localStorage ممکن است به تبی
     اشاره کند که دیگر وجود ندارد. */
  const TAB_ALIAS = { shortage: "review", cardex: "review" };
  const curTab = TAB_ALIAS[activeTab] || (tabContent[activeTab] ? activeTab : "import");
  const isMain = MAIN_TABS.some((m) => m.value === curTab);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
        <Space>
          <Title level={5} style={{ margin: 0 }}>حضور و غیاب</Title>
          <HelpPopover title="ترتیبِ کار در این صفحه" content={
            <div style={{ fontSize: 13, lineHeight: 2 }}>
              <ol style={{ paddingInlineStart: 18, margin: 0 }}>
                <li><b>فایلِ دستگاه را بارگذاری کنید</b> (کارتِ بالای صفحه). اول
                  «پیش‌نمایش» تا ببینید چند پانچ و چند شناسه دارد.</li>
                <li><b>شناسه‌های بی‌صاحب را به پرسنل وصل کنید.</b> کدِ هر نفر در
                  دستگاه ثابت است؛ اگر اسمِ کسی عوض شده فقط نگاشتِ همان کد را
                  به پرسنلِ درست تغییر دهید — تاریخچه‌اش حفظ می‌شود.</li>
                <li><b>روزهای مشکل‌دار را نفربه‌نفر رسیدگی کنید</b> — ناقص،
                  چندپانچ، غیبت. همان‌جا ورود/خروج را اصلاح می‌کنید، می‌بخشید،
                  یا غیبت/کسرکار ثبت می‌کنید.</li>
                <li><b>گزارشِ دوره‌ای</b> را بگیرید (همان فرمِ کاغذی) و وقتی
                  «باقی‌ماندهٔ بررسی» همه صفر شد، ماه را نهایی کنید.</li>
              </ol>
              <div style={{ marginTop: 6 }}>
                فقط پرسنلی که در لیستِ پرسنل تیکِ <b>«حضوری»</b> دارند اینجا
                می‌آیند. کارمزدی‌ها حقوقشان از تولید می‌آید و کسرِ حضور نمی‌خورند.
              </div>
            </div>
          } />
        </Space>
      </div>

      {previewModal}

      {/* نوارِ مراحل + ماه — چسبیده به بالا، چون در هر شش مرحله لازم است.
          قبلاً انتخابگرِ ماه داخلِ `extra`ی شش کارتِ مختلف تکرار شده بود و در
          سه تب اصلاً وجود نداشت؛ کاربر باید دنبالش می‌گشت. */}
      <div className="gx-sticky-bar" style={{
        position: "sticky", top: "calc(64px + env(safe-area-inset-top, 0px))", zIndex: 9,
        background: "var(--bg, #eef1f6)", padding: "8px 0", marginBottom: 12,
        display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center",
      }}>
        {/* گوشی (v2.132.0): نوارِ مراحل ۸۶۰px بود و کلِ صفحه را افقی اسکرول می‌داد ⇒ فقط خودِ نوار اسکرول می‌خورد */}
        <div style={{ maxWidth: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <Segmented size="large" value={isMain ? curTab : ""} options={MAIN_TABS}
            onChange={goTab} />
        </div>
        <Dropdown menu={{ items: MORE_TABS.map((m) => ({ key: m.key, label: m.label })),
          onClick: ({ key }) => goTab(key) }}>
          <Button size="large">بیشتر <DownOutlined /></Button>
        </Dropdown>
        {!isMain && <Tag color="blue" style={{ fontSize: 13 }}>{MORE_TABS.find((m) => m.key === curTab)?.label}</Tag>}
        <div style={{ marginInlineStart: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          {monthData && (
            <Space size={4}>
              <Tag color={monthData.punches ? "blue" : "default"}>پانچ {fa(monthData.punches || 0)}</Tag>
              <Tag>روز {fa(monthData.days || 0)}</Tag>
              <Tag>پرسنل {fa(monthData.employees || 0)}</Tag>
            </Space>
          )}
          {periodSelector}
        </div>
      </div>

      <div>{tabContent[curTab]}</div>

      <Modal
        title="افزودن دستیِ رکورد حضور"
        open={addOpen}
        onOk={() => submitAdd()}
        onCancel={() => setAddOpen(false)}
        okText="ثبت" cancelText="انصراف"
      >
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message="یک رکوردِ حضور را دستی ثبت کنید. اگر برای همان نفر و همان تاریخ رکوردی باشد، به‌روزرسانی می‌شود. کارکرد و کسری خودکار حساب می‌شود. (تاریخ خالی = امروز)" />
        <Form form={addForm} layout="vertical" style={{ direction: "rtl" }}>
          <Form.Item name="employee_id" label="پرسنل" rules={[{ required: true, message: "پرسنل را انتخاب کنید" }]}>
            <Select showSearch placeholder="انتخاب پرسنل" optionFilterProp="children"
              filterOption={filterByChildren}>
              {employees.map(e => <Option key={e.id} value={e.id}>{e.full_name} ({e.employee_code})</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="work_date" label="تاریخ (خالی = امروز)" rules={[{ required: true, message: "تاریخ را انتخاب کنید" }]}>
            <JalaliDateInput />
          </Form.Item>
          <Space size="large">
            <Form.Item name="check_in" label="ساعت ورود">
              <TimePicker format="HH:mm" minuteStep={1} allowClear />
            </Form.Item>
            <Form.Item name="check_out" label="ساعت خروج">
              <TimePicker format="HH:mm" minuteStep={1} allowClear />
            </Form.Item>
          </Space>
          <Form.Item name="note" label="یادداشت (اختیاری)">
            <Input placeholder="مثلاً: ثبت دستی به‌جای پانچِ جاافتاده" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="ویرایش رکورد حضور"
        open={!!editRec}
        onOk={handleSaveEdit}
        onCancel={() => setEditRec(null)}
        okText="ذخیره" cancelText="انصراف"
      >
        <Form form={form} layout="vertical" style={{ direction: "rtl" }}>
          <Form.Item name="check_in" label="ساعت ورود">
            <TimePicker format="HH:mm" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="check_out" label="ساعت خروج">
            <TimePicker format="HH:mm" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="status" label="وضعیت">
            <Select>
              <Option value="present">حاضر</Option>
              <Option value="absent">غایب</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`نگاشت شناسهٔ دستگاه ${mapModal.device_uid} به پرسنل`}
        open={mapModal.open}
        onOk={handleMapDevice}
        onCancel={() => { setMapModal({ open: false, device_uid: null }); setMapEmpId(null); }}
        okText="نگاشت و بازپردازش" cancelText="انصراف"
      >
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message="پس از نگاشت، پانچ‌های این شناسه به پرسنل انتخاب‌شده نسبت داده شده و رکوردهای روزانهٔ حضور ساخته می‌شوند." />
        <div style={{ direction: "rtl" }}>
          <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
            پرسنل (با نام یا کدِ پرسنلیِ ۵رقمی جست‌وجو کنید):
          </label>
          <Select
            showSearch value={mapEmpId} onChange={setMapEmpId}
            style={{ width: "100%" }} placeholder="نام یا کد پرسنلی..."
            optionFilterProp="children"
            filterOption={filterByChildren}
          >
            {mapCandidates.map(e => (
              <Option key={e.id} value={e.id}>
                {e.full_name} ({e.employee_code}){e.__missing ? " ← این ماه ترددی ندارد" : ""}
              </Option>
            ))}
          </Select>
        </div>
      </Modal>

      <Modal
        title={`نام برای شناسهٔ دستگاه ${labelModal.device_uid}`}
        open={labelModal.open}
        onOk={handleSaveLabel}
        onCancel={() => { setLabelModal({ open: false, device_uid: null }); setLabelText(""); }}
        okText="ذخیره" cancelText="انصراف"
      >
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message="یک نامِ حدسی برای این شناسه بنویسید (مثلاً «شاید علی احمدی»). این فقط یک برچسب برای شناسایی است و پرسنل نمی‌سازد." />
        <div style={{ direction: "rtl" }}>
          <Input value={labelText} onChange={e => setLabelText(e.target.value)}
            placeholder="نام حدسی" autoFocus onPressEnter={handleSaveLabel} allowClear />
        </div>
      </Modal>

      <Modal title="راهنمای بازبینیِ حضور ماهانه" open={helpOpen} footer={null}
        onCancel={() => setHelpOpen(false)} width={640}>
        <div style={{ direction: "rtl", lineHeight: 2 }}>
          <p><b>این صفحه برای چیست؟</b> پیش از نهایی‌کردنِ حقوقِ ماه، حسابدار باید روزهای مشکل‌دارِ حضور را بررسی و برای هرکدام تصمیم بگیرد.</p>
          <p><b>معنی وضعیت‌ها:</b></p>
          <ul style={{ paddingInlineStart: 20 }}>
            <li><b>غیبت:</b> هیچ انگشتی در روزِ کاری ثبت نشده (فقط صفر پانچ = غیبت).</li>
            <li><b>ناقص:</b> فقط یک انگشت ثبت شده — <u>غیبت نیست</u>؛ احتمالاً ورود یا خروج جا افتاده.</li>
            <li><b>چندپانچ:</b> بیش از دو انگشت ثبت شده؛ نیازمند بررسی.</li>
            <li><b>کسری:</b> حضورِ کمتر از برنامهٔ کاری (کسری روزانهٔ کمتر از آستانه نادیده می‌شود).</li>
            <li><b>جریمهٔ تعطیل:</b> غیبتِ کنارِ یک تعطیلی.</li>
          </ul>
          <p><b>چطور بازبینی کنید؟</b> برای هر ردیف از ستون «تصمیم بازبینی» یکی را انتخاب کنید (تأیید/ورود فراموش‌شده/غیبت موجه/مأموریت/نادیده/…). تا قبل از «تأیید نهایی حضور ماه»، تصمیم‌ها قابلِ تغییرند.</p>
          <p><b>نهایی‌سازی:</b> دکمهٔ «تأیید نهایی حضور ماه» وقتی همهٔ موارد تصمیم‌گیری شدند فعال می‌شود. بعد از نهایی‌سازی، ویرایش نیاز به کد مدیریتی دارد. <b>یک کلیک اشتباه برگشت‌ناپذیر نیست</b> — هر تصمیم تا نهایی‌سازی قابلِ تغییر است.</p>
          <p><b>اصلاح دستی:</b> اگر ورود یا خروجِ کسی فراموش شده، با دکمهٔ «ورود/خروج» همان‌جا ساعت را وارد/اصلاح کنید؛ کارکرد و کسری خودکار بازمحاسبه می‌شود (نیازی به رفتن به صفحهٔ دیگر نیست).</p>
        </div>
      </Modal>

    </div>
  );
}
